import { useEffect, useState } from "react";

/**
 * A value that follows its source a fixed delay behind.
 *
 * The donor's `useDebounce`, which every one of its paginated selects wraps its
 * search box in: `useDebounce(filter, 200)` in `CustomSelectWithPagination.tsx:43`
 * and `useDebounce(inputValue, 200)` in `CategoriesFieldPaginated.tsx:47`. There
 * the delay pays for itself — the debounced value is a query argument, and
 * typing would otherwise fire a paginated round trip per keystroke.
 *
 * **Here it pays for itself the same way** (since #412): the lookup menus key
 * their paginated read on the debounced term, so the delay is again the thing
 * standing between typing and a round trip per keystroke. It buys the visible
 * behaviour either way — the donor's narrowing runs off the debounced value too
 * (`CustomSelectWithPagination.tsx:101-111` filters on `debouncedFilter`, and
 * the category tree is rebuilt from the query the same value keys), so the rows
 * stand still for 200ms after a keystroke and then jump. That is behaviour a
 * modsy user sees, and #253 names the interval.
 *
 * The first value is not delayed: the debounce is about *changes*, and an
 * initial render has none to wait on.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (value === settled) return;
    const pause = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(pause);
  }, [value, settled, delayMs]);

  return settled;
}
